import { supabase } from "@/lib/supabase"

export class ApiError extends Error {
  readonly status: number
  constructor(status: number) {
    super(`Request failed with status ${status}`)
    this.status = status
  }
}

const authedRequest = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  const { data } = await supabase.auth.getSession()
  const token = data.session?.access_token

  if (!token) throw new Error("No access token found")

  const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}${url}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  })

  if (!response.ok) throw new ApiError(response.status)

  return response
}

export const getJSON = <T>(url: string): Promise<T> =>
  authedRequest(url).then((r) => r.json())

export const postJSON = async <T = void>(
  url: string,
  body?: unknown
): Promise<T> => {
  const response = await authedRequest(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })
  if (response.status === 204) return undefined as T
  return response.json()
}

export const patchJSON = async <T = void>(
  url: string,
  body?: unknown
): Promise<T> => {
  const response = await authedRequest(url, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })
  if (response.status === 204) return undefined as T
  return response.json()
}

export const deleteJSON = (url: string): Promise<void> =>
  authedRequest(url, { method: "DELETE" }).then(() => undefined)

export const postPublicJSON = async <T = void>(
  url: string,
  body?: unknown
): Promise<T> => {
  const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}${url}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })
  if (!response.ok) throw new ApiError(response.status)
  if (response.status === 204) return undefined as T
  return response.json()
}
